import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import { Resource } from '../Resource';
import { BillingStatus } from '../enums';
import * as methods from '../methods';
const rootPath = 'billing';
// TODO: ensure mapping
/** The kind of invoicing recurrence */
export var InvoiceKind;
(function (InvoiceKind) {
    InvoiceKind[InvoiceKind["anniversary"] = 0] = "anniversary";
    InvoiceKind[InvoiceKind["oneTime"] = 1] = "oneTime";
})(InvoiceKind || (InvoiceKind = {}));
// TODO: ensure mapping
/** Whether this {@link Invoice} concern an option or a subscription plan */
export var InvoiceDetailItemKind;
(function (InvoiceDetailItemKind) {
    InvoiceDetailItemKind[InvoiceDetailItemKind["plus"] = 0] = "plus";
    InvoiceDetailItemKind[InvoiceDetailItemKind["expert"] = 1] = "expert";
    InvoiceDetailItemKind[InvoiceDetailItemKind["option"] = 2] = "option";
    InvoiceDetailItemKind[InvoiceDetailItemKind["credit"] = 3] = "credit";
})(InvoiceDetailItemKind || (InvoiceDetailItemKind = {}));
/** See {@link https://gitlab.com/weenat-back/core/-/blob/master/apps/billing/constants.py?ref_type=heads#L674} */
export var SubscriptionQuoteKind;
(function (SubscriptionQuoteKind) {
    SubscriptionQuoteKind[SubscriptionQuoteKind["own"] = 0] = "own";
    SubscriptionQuoteKind[SubscriptionQuoteKind["memberInvitation"] = 1] = "memberInvitation";
    SubscriptionQuoteKind[SubscriptionQuoteKind["adherentInvitation"] = 2] = "adherentInvitation";
})(SubscriptionQuoteKind || (SubscriptionQuoteKind = {}));
// TODO: ensure mapping
/** Wether the invoice is payed or not */
export var InvoiceStatus;
(function (InvoiceStatus) {
    InvoiceStatus[InvoiceStatus["notPayed"] = 0] = "notPayed";
    InvoiceStatus[InvoiceStatus["payed"] = 1] = "payed";
    InvoiceStatus[InvoiceStatus["creditNote"] = 2] = "creditNote";
    // front status so use big number to avoid collision with back one
    InvoiceStatus[InvoiceStatus["next"] = 100] = "next";
})(InvoiceStatus || (InvoiceStatus = {}));
const isRawInvoice = (value) => {
    return isNil(value.line_details) && !isNil(value.number);
};
function parse(x) {
    const result = {
        kind: x.invoice_type,
        dueDate: x.date_due,
        invoiceDate: x.date_invoice,
        paymentLink: x.payment_link,
        downloadLink: x.download_link,
        amount: x.amount_untaxed,
        amountWithTaxes: x.amount_total
    };
    if (isRawInvoice(x)) {
        const invoice = { ...result, reference: x.number, status: x.state };
        return invoice;
    }
    else {
        const nextInvoice = {
            ...result,
            detail: x.line_details?.map(fromRaw.getInvoiceDetailItem) ?? [],
            reference: null,
            status: null
        };
        return nextInvoice;
    }
}
export const fromRaw = {
    getCurrentPlan: (x) => ({
        billingStatus: x.billing_status,
        /** Licences linked to this contract */
        licencesCount: x.licence_count,
        price: x.recurring_total,
        priceWithTaxes: x.recurring_amount_total,
        reference: x.code,
        startDate: x.start,
        renewalDate: x.until,
        detail: x.licence_details.reduce((acc, detail) => {
            acc[detail.billing_status] = {
                licencesCount: detail.licence_count,
                priceSubTotal: detail.price_subtotal,
                unitDiscountPrice: detail.price_discount,
                unitPublicPrice: detail.price_unit
            };
            return acc;
        }, {})
    }),
    getInvoiceDetailItem: (x) => ({
        description: x.description,
        kind: x.kind,
        userName: x.user_name,
        orgName: x.organization_name,
        publicPrice: x.price_unit,
        subTotalPrice: x.price_subtotal,
        optionCode: x.option_code,
        membershipCode: x.code
    }),
    getInvoice: (x) => parse(x),
    getNextInvoice: (x) => parse(x),
    getPaymentIncident: (raw) => ({
        paymentDueDate: raw.payment_due_date
    }),
    getInvoices: (xs) => xs.map((x) => fromRaw.getInvoice(x)),
    getPlanFeaturesByBilling: (x) => ({
        essential: x[BillingStatus.essential],
        expert: x[BillingStatus.expert],
        plus: x[BillingStatus.plus]
    }),
    getAccount: ({ address_city, address_line_1, address_line_2, address_zip, country, id, name, organization, siret, vat_number }) => ({
        addressLine1: address_line_1,
        addressLine2: address_line_2,
        city: address_city,
        country: isEmpty(country) ? null : country,
        id,
        organizationName: name,
        organizationId: organization,
        siret,
        zipCode: address_zip,
        vatNumber: vat_number
    }),
    getUpgradeNetOrgSubscriptionPrices: (price) => ({
        sumTaxlessPrice: price.amount_untaxed,
        sumTotalPrice: price.amount_total,
        linesByUser: price.lines_by_user.map((lbu) => ({
            previousPlanDateStart: lbu.previous_start,
            creditDateEnd: lbu.credit_end,
            creditDateStart: lbu.credit_start,
            consumed: {
                status: lbu.consumed.status,
                priceUnit: lbu.consumed.price_unit,
                priceSubtotal: lbu.consumed.price_subtotal,
                priceTotal: lbu.consumed.price_total,
                tax: lbu.consumed.tax
            },
            unconsumed: {
                status: lbu.unconsumed.status,
                priceUnit: lbu.unconsumed.price_unit,
                priceSubtotal: lbu.unconsumed.price_subtotal,
                priceTotal: lbu.unconsumed.price_total,
                tax: lbu.unconsumed.tax
            },
            name: lbu.name
        }))
    }),
    getSubscriptionPrice: (price) => ({
        billingStatus: price.status,
        taxlessPrice: price.price_subtotal,
        totalPrice: price.price_total
    }),
    getSubscriptionOrder: (x) => ({
        billingStatus: x.status,
        saleOrderId: x.sale_order_id,
        paymentLink: x.payment_link,
        priceSubtotal: x.price_subtotal,
        priceTotal: x.price_total
    }),
    getSubscriptionPrices: (prices) => prices.map(fromRaw.getSubscriptionPrice),
    setup: ({ client_secret, id }) => ({
        id,
        clientSecret: client_secret
    }),
    paymentMethod: ({ id, card_brand, card_exp_month, card_exp_year, card_holder_name, card_last4 }) => ({
        id,
        brand: card_brand,
        expirationMonth: card_exp_month,
        expirationYear: card_exp_year,
        holderName: card_holder_name,
        last4Digits: card_last4
    })
};
export const toRaw = {
    billingStatus: ({ billingStatus }) => ({
        licence: billingStatus
    }),
    editAccount: ({ addressLine1, addressLine2, city, country, organizationName, siret, zipCode, vatNumber }) => ({
        address_line_1: addressLine1,
        address_line_2: addressLine2,
        address_city: city,
        country,
        name: organizationName,
        siret,
        address_zip: zipCode,
        vat_number: vatNumber
    })
};
const billing = Resource((requesterBuilder, config) => {
    const getPaymentMethods = (orgId) => requesterBuilder({
        path: `${rootPath}/${orgId}/paymentmethods`,
        requestCtx: {
            transform: (results) => results ? results.map(fromRaw.paymentMethod) : []
        }
    });
    const getAccount = (orgId) => requesterBuilder({
        path: `${rootPath}/${orgId}/account`,
        requestCtx: {
            method: 'GET',
            transform: fromRaw.getAccount
        }
    });
    return {
        getPlanFeaturesByBilling: () => requesterBuilder({
            path: `${rootPath}/info`,
            requestCtx: {
                method: 'GET',
                transform: fromRaw.getPlanFeaturesByBilling
            }
        }),
        confirm: (orgId) => requesterBuilder({
            path: `${rootPath}/${orgId}/paymentmethods/confirm`,
            requestCtx: {
                method: 'POST',
                transform: fromRaw.paymentMethod
            }
        }),
        sendPlanSubscribeMail: (orgId) => requesterBuilder({
            path: `${rootPath}/${orgId}/account/sav/open`,
            requestCtx: {
                method: 'POST',
                transform: () => ({}),
                toRaw: toRaw.billingStatus
            }
        }),
        sendPlanUnsubscribeMail: (orgId) => requesterBuilder({
            path: `${rootPath}/${orgId}/account/sav/close`,
            requestCtx: {
                method: 'POST',
                transform: () => ({}),
                toRaw: toRaw.billingStatus
            }
        }),
        editAccount: (orgId) => requesterBuilder({
            path: `${rootPath}/${orgId}/account`,
            requestCtx: {
                method: 'PATCH',
                transform: fromRaw.getAccount,
                toRaw: toRaw.editAccount
            },
            keysToInvalidate: [getAccount(orgId).invalidationKey]
        }),
        getCurrentPlan: (orgId) => methods.get({
            requesterBuilder,
            rootPath: `${rootPath}/${orgId}/plan/current`,
            fromRaw: fromRaw.getCurrentPlan
        })(),
        getAccount,
        getPaymentMethods,
        deletePaymentMethod: (orgId, paymentMethodId) => methods.deletion({
            rootPath: `${rootPath}/paymentmethods`,
            requesterBuilder,
            buildKeysToInvalidate: () => [getPaymentMethods(orgId).invalidationKey]
        })(paymentMethodId),
        upgrade: (orgId) => requesterBuilder({
            path: `${rootPath}/${orgId}/account/upgrade`,
            requestCtx: {
                method: 'POST',
                toRaw: toRaw.billingStatus
            }
        }),
        getInvoices: (orgId, query) => methods.get({
            requesterBuilder,
            host: config.getCtx().hosts.tiles,
            rootPath: `${rootPath}/${orgId}/invoices`,
            fromRaw: fromRaw.getInvoices,
            params: {
                query
            }
        })(),
        getNextInvoice: (orgId, query) => methods.get({
            requesterBuilder,
            host: config.getCtx().hosts.tiles,
            rootPath: `${rootPath}/${orgId}/invoices/next`,
            fromRaw: fromRaw.getNextInvoice,
            params: {
                query: {
                    invoice_type: query?.invoiceType
                }
            }
        })(),
        getPaymentIncident: (orgId) => methods.get({
            requesterBuilder,
            host: config.getCtx().hosts.tiles,
            rootPath: `${rootPath}/${orgId}/paymentincident`,
            fromRaw: fromRaw.getPaymentIncident
        })(),
        getSubscriptionOrder: (orgId, billingStatus) => methods.get({
            requesterBuilder,
            host: config.getCtx().hosts.tiles,
            rootPath: `${rootPath}/${orgId}/subscription/order/${billingStatus}`,
            fromRaw: fromRaw.getSubscriptionOrder
        })(),
        getSubscriptionPrices: (orgId, kind = SubscriptionQuoteKind.own, 
        /** Needed with {@link SubscriptionQuoteKind.memberInvitation} & {@link SubscriptionQuoteKind.adherentInvitation} */
        email = undefined) => methods.get({
            requesterBuilder,
            host: config.getCtx().hosts.tiles,
            rootPath: `${rootPath}/${orgId}/subscription/prices`,
            params: {
                query: isNil(email) ? { kind } : { kind, email }
            },
            fromRaw: fromRaw.getSubscriptionPrices
        })(),
        /** @todo Not yet available on api,the response need spec */
        getUpgradeNetOrgSubscriptionPrices: (orgId, netOrgId, billingStatus) => methods.get({
            requesterBuilder,
            host: config.getCtx().hosts.tiles,
            rootPath: `${rootPath}/${orgId}/upgrade/${netOrgId}/subscription/${billingStatus}/prices`,
            fromRaw: fromRaw.getUpgradeNetOrgSubscriptionPrices
        })()
    };
});
export default billing;
